import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { Plus, Pencil, Trash2, Search, X, Megaphone } from 'lucide-react';
import { db } from '../database/supabase';
import { useAuth } from '../contexts/AuthContext';

const PRIORITIES = [
  { value: 'high', label: 'Important' },
  { value: 'normal', label: 'News' },
  { value: 'low', label: 'Low' },
];

const emptyForm = {
  title: '',
  content: '',
  priority: 'normal',
  is_published: true,
};

export default function AnnouncementsManagement() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const loadAnnouncements = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: err } = await db.getAnnouncements(false);
      if (err) throw err;
      setItems(data || []);
    } catch (err) {
      console.error(err);
      setError(err.message || 'Failed to load announcements');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAnnouncements();
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setModalOpen(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    setForm({
      title: item.title || '',
      content: item.content || '',
      priority: item.priority || 'normal',
      is_published: item.is_published !== false,
    });
    setModalOpen(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        title: form.title.trim(),
        content: form.content.trim(),
        priority: form.priority,
        is_published: form.is_published,
        updated_at: new Date().toISOString(),
      };

      if (editing) {
        const { data, error: err } = await db.updateAnnouncement(editing.id, payload);
        if (err) throw err;
        setItems((prev) => prev.map((a) => (a.id === editing.id ? { ...a, ...data } : a)));
        toast.success('Announcement updated');
      } else {
        const { data, error: err } = await db.createAnnouncement({
          ...payload,
          author_id: user?.id && user.id !== '1' ? user.id : null,
          created_at: new Date().toISOString(),
          published_at: form.is_published ? new Date().toISOString() : null,
        });
        if (err) throw err;
        setItems((prev) => [data, ...prev]);
        toast.success('Announcement created');
      }
      setModalOpen(false);
    } catch (err) {
      console.error(err);
      toast.error(err.message || 'Failed to save announcement');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Delete announcement "${item.title}"?`)) return;
    try {
      const { error: err } = await db.deleteAnnouncement(item.id);
      if (err) throw err;
      setItems((prev) => prev.filter((a) => a.id !== item.id));
      toast.success('Announcement deleted');
    } catch (err) {
      console.error(err);
      toast.error(err.message || 'Failed to delete announcement');
    }
  };

  const togglePublished = async (item) => {
    try {
      const next = !item.is_published;
      const { data, error: err } = await db.updateAnnouncement(item.id, {
        is_published: next,
        published_at: next ? new Date().toISOString() : null,
        updated_at: new Date().toISOString(),
      });
      if (err) throw err;
      setItems((prev) => prev.map((a) => (a.id === item.id ? { ...a, ...data } : a)));
      toast.success(next ? 'Published' : 'Unpublished');
    } catch (err) {
      console.error(err);
      toast.error(err.message || 'Failed to update status');
    }
  };

  const filtered = items.filter((a) => {
    if (statusFilter === 'published' && !a.is_published) return false;
    if (statusFilter === 'draft' && a.is_published) return false;
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      (a.title || '').toLowerCase().includes(q) ||
      (a.content || '').toLowerCase().includes(q)
    );
  });

  const priorityBadge = (priority) => {
    if (priority === 'high') return 'bg-yellow-100 text-yellow-800';
    if (priority === 'low') return 'bg-gray-100 text-gray-800';
    return 'bg-blue-100 text-blue-800';
  };

  const priorityLabel = (priority) => {
    return PRIORITIES.find((p) => p.value === priority)?.label || 'News';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-800" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <h3 className="text-lg font-medium text-red-800 mb-2">Error Loading Announcements</h3>
        <p className="text-red-600 mb-4">{error}</p>
        <button type="button" onClick={loadAnnouncements} className="btn-primary">
          Retry
        </button>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Announcements</h1>
          <p className="text-gray-600">Create and publish news for the public site</p>
        </div>
        <button type="button" onClick={openCreate} className="btn-primary inline-flex items-center">
          <Plus className="w-4 h-4 mr-2" />
          New Announcement
        </button>
      </div>

      <div className="bg-white rounded-lg shadow mb-6 p-4 flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            className="form-input pl-10"
            placeholder="Search announcements..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <select
          className="form-input max-w-xs"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="">All Status</option>
          <option value="published">Published</option>
          <option value="draft">Draft</option>
        </select>
      </div>

      <div className="grid gap-4">
        {filtered.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-10 text-center text-gray-500">
            <Megaphone className="w-10 h-10 mx-auto mb-3 text-gray-300" />
            No announcements found
          </div>
        ) : (
          filtered.map((item) => (
            <div key={item.id} className="bg-white rounded-lg shadow p-6">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2 mb-2">
                    <span className={`status-badge ${priorityBadge(item.priority)}`}>
                      {priorityLabel(item.priority)}
                    </span>
                    <span
                      className={`status-badge ${
                        item.is_published
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {item.is_published ? 'Published' : 'Draft'}
                    </span>
                    <span className="text-sm text-gray-500">
                      {item.created_at
                        ? new Date(item.created_at).toLocaleDateString()
                        : ''}
                    </span>
                    {item.users?.name && (
                      <span className="text-sm text-gray-400">by {item.users.name}</span>
                    )}
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{item.title}</h3>
                  <p className="text-gray-600 whitespace-pre-wrap">{item.content}</p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => togglePublished(item)}
                    className="btn-outline text-sm py-2 px-3"
                  >
                    {item.is_published ? 'Unpublish' : 'Publish'}
                  </button>
                  <button
                    type="button"
                    onClick={() => openEdit(item)}
                    className="p-2 text-blue-800 hover:bg-blue-50 rounded"
                    title="Edit"
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDelete(item)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded"
                    title="Delete"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {modalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-medium text-gray-900">
                {editing ? 'Edit Announcement' : 'New Announcement'}
              </h3>
              <button type="button" onClick={() => setModalOpen(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-6 h-6" />
              </button>
            </div>
            <form onSubmit={handleSave} className="p-6 space-y-4">
              <div>
                <label className="form-label">Title *</label>
                <input
                  className="form-input"
                  required
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                />
              </div>
              <div>
                <label className="form-label">Content *</label>
                <textarea
                  className="form-input min-h-[140px]"
                  required
                  value={form.content}
                  onChange={(e) => setForm({ ...form, content: e.target.value })}
                />
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="form-label">Priority</label>
                  <select
                    className="form-input"
                    value={form.priority}
                    onChange={(e) => setForm({ ...form, priority: e.target.value })}
                  >
                    {PRIORITIES.map((p) => (
                      <option key={p.value} value={p.value}>
                        {p.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex items-end pb-2">
                  <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      checked={form.is_published}
                      onChange={(e) => setForm({ ...form, is_published: e.target.checked })}
                    />
                    Publish immediately
                  </label>
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => setModalOpen(false)} className="btn-outline">
                  Cancel
                </button>
                <button type="submit" disabled={saving} className="btn-primary">
                  {saving ? 'Saving...' : editing ? 'Save Changes' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
